// src/components/probe/ProbeEventDetails.tsx
import React, { useEffect, useCallback, useState } from 'react';
import { LogRecord } from '../../models/Probe';
import ActionButtons from './ActionButtons';
import SyntaxHighlighter from 'react-syntax-highlighter'
import { atomOneDark } from 'react-syntax-highlighter/dist/esm/styles/hljs';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronLeft, faChevronRight, faTable, faCode } from '@fortawesome/free-solid-svg-icons';

interface ProbeEventDetailsProps {
  event: LogRecord | null;
  currentIndex: number | null;
  totalEvents: number;
  onNavigate: (newIndex: number) => void;
}

const FieldsView: React.FC<{ event: LogRecord }> = ({ event }) => {
  const fieldsToShow = [
    { key: 'message', label: 'Message' },
    { key: 'timestamp', label: 'Timestamp' },
    { key: 'severity', label: 'Severity' },
    { key: 'data.container_name', label: 'Service' },
    { key: 'traceid', label: 'Trace ID' },
    { key: 'data.data.span_id', label: 'Span ID' },
    { key: 'data.traceparent', label: 'Traceparent' },
    { key: 'data.grpc_code', label: 'gRPC Code' },
    { key: 'data.grpc_error', label: 'gRPC Error' },
    { key: 'data.grpc_error_details', label: 'gRPC Error Details' },
    { key: 'data.grpc_service', label: 'gRPC Service' },
    { key: 'data.grpc_method', label: 'gRPC Method' },
    { key: 'data.grpc_request_deadline', label: 'gRPC Request Deadline' },
    { key: 'data.grpc_duration_ms', label: 'gRPC Duration (ms)' },
    { key: 'data.grpc_start_time', label: 'gRPC Start Time' },
    { key: 'data.http_path', label: 'HTTP Path' },
    { key: 'data.device_ip_address', label: 'Device IP Address' },
    { key: 'data.caller', label: 'Caller' },
    { key: 'data.args', label: 'Arguments' },
    { key: 'data.sql', label: 'SQL' },    
  ];

  const renderValue = (value: any) => {
    if (typeof value === 'object' && value !== null) {
      return (
        <SyntaxHighlighter language="json" style={atomOneDark} customStyle={{ background: 'transparent', padding: 0, margin: 0 }}>
          {JSON.stringify(value, null, 2)}
        </SyntaxHighlighter>
      );
    }
    return String(value);
  };

  const getValue = (obj: any, path: string) => {
    return path.split('.').reduce((p, c) => (p && p[c]) ? p[c] : null, obj);
  };

  return (
    <table className="w-full text-left table-fixed">
      <tbody>
        {fieldsToShow.map(({ key, label }) => {
          const value = getValue(event, key);
          if (value === null || value === undefined) return null;

          return (
            <tr key={key} className="border-b border-border-dark last:border-b-0 odd:bg-background-darker even:bg-background-light">
              <td className="p-2 font-semibold text-text-dark w-32 align-top">{label}</td>
              <td className="p-2 text-text-light break-words align-top">{renderValue(value)}</td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
};

const ProbeEventDetails: React.FC<ProbeEventDetailsProps> = ({ event, currentIndex, totalEvents, onNavigate }) => {
  const [viewMode, setViewMode] = useState<'table' | 'json'>('table');

  const jsonString = JSON.stringify(event, null, 2);

  const handlePrev = useCallback(() => {
    if (currentIndex !== null && currentIndex > 0) {
      onNavigate(currentIndex - 1);
    }
  }, [currentIndex, onNavigate]);

  const handleNext = useCallback(() => {
    if (currentIndex !== null && currentIndex < totalEvents - 1) {
      onNavigate(currentIndex + 1);
    }
  }, [currentIndex, totalEvents, onNavigate]);

  useEffect(() => {
    const handleKeyBindings = (e: KeyboardEvent) => {
      if (e.key === 'ArrowLeft') {
        e.preventDefault();
        handlePrev();
      } else if (e.key === 'ArrowRight') {
        e.preventDefault();
        handleNext();
      }
    };

    window.addEventListener('keydown', handleKeyBindings);
    return () => {
      window.removeEventListener('keydown', handleKeyBindings);
    };
  }, [handlePrev, handleNext]);

  if (!event || currentIndex === null) {
    return (
      <div className="p-4 text-text-dark text-center">
        Select an event from the timeline to view its details.
      </div>
    );
  }

  const getSelectedClassName = (viewMode: 'table' | 'json', viewType: 'table' | 'json') => {
    return viewMode === viewType ? 'bg-transparent text-accent-color' : 'bg-transparent text-text-dark hover:bg-background-light';
  }

  return (
    <div className="p-4 h-full flex flex-col">
      <div className="flex justify-between items-center mb-3 flex-shrink-0">
        <h3 className="text-lg font-semibold text-text-light">Event Details</h3>
        <div className="flex items-center space-x-4 bg-background-light bg-border-light rounded-md p-1">
          {/* View Toggle */}
          <div className="flex items-center bg-background-light rounded-md p-1 space-x-1">
            <button onClick={() => setViewMode('table')} title="Fields View" 
              className={`${getSelectedClassName(viewMode, 'table')} text-xl border-none px-2 py-1 rounded transition-colors `}>
              <FontAwesomeIcon icon={faTable} />
            </button>
            <button onClick={() => setViewMode('json')} title="JSON View" 
              className={`${getSelectedClassName(viewMode, 'json')} text-xl border-none px-2 py-1 rounded transition-colors`}>
              <FontAwesomeIcon icon={faCode} />
            </button>
          </div>
          <span className="text-sm text-text-dark">|</span>
          {/* Navigation Buttons */}
          <div className="flex items-center space-x-2">
            <button onClick={handlePrev} disabled={currentIndex <= 0} className="icon-button disabled:opacity-50 disabled:cursor-not-allowed" title="Previous Event">
              <FontAwesomeIcon icon={faChevronLeft} />
            </button>
            <span className="text-sm text-text-dark">{currentIndex + 1} / {totalEvents}</span>
            <button onClick={handleNext} disabled={currentIndex >= totalEvents - 1} className="icon-button disabled:opacity-50 disabled:cursor-not-allowed" title="Next Event">
              <FontAwesomeIcon icon={faChevronRight} />
            </button>
          </div>
          <span className="text-sm text-text-dark">|</span>          
          {/* Action Buttons */}
          <ActionButtons
            onCopy={() => {}} // No specific action needed here, handled by ActionButtons
            dataToCopy={jsonString}
          />
        </div>
      </div>
      <div className="bg-background-darker p-3 rounded-lg overflow-y-auto text-sm flex-1 min-h-0">
        {viewMode === 'table' ? (
          <FieldsView event={event} />
        ) : (
          <SyntaxHighlighter language="json" style={atomOneDark} customStyle={{ background: 'transparent', padding: 0 }} className="syntax-highlighter-code">
            {jsonString}
          </SyntaxHighlighter>
        )}
      </div>
    </div>
  );
};

export default ProbeEventDetails;